<script setup lang="ts">
import type { IconName } from "../Icon/icons";
import { computed, useAttrs } from "vue";
import VIcon from "../Icon/VIcon.vue";
import VTooltip from "../Tooltip/VTooltip.vue";

// Attributes such as data-testid and aria-describedby belong on the button; class and style size the outer wrapper.
defineOptions({ inheritAttrs: false });

const {
  icon,
  label,
  active = false,
  text = null,
  hasPopup = false,
  toggle = true,
  disabled = false,
  loading = false,
} = defineProps<{
  icon: IconName
  /** Tooltip content and accessible name of the trigger. When disabled, pass the reason instead. */
  label: string
  active?: boolean
  /** Short value next to the icon, such as a selected date range. The trigger widens to fit it. */
  text?: string | null
  hasPopup?: boolean
  /** Announce the trigger as a toggle (`aria-pressed`). Turn off for a plain action such as an export. */
  toggle?: boolean
  /** Dimmed and not clickable, but still focusable so the tooltip can explain why. */
  disabled?: boolean
  /** Shows a spinner instead of the icon and ignores clicks while the action runs. */
  loading?: boolean
}>();

const emit = defineEmits<{
  click: [event: MouseEvent]
}>();

defineSlots<{
  /** Absolutely positioned overlay on the trigger, such as an active filter count. */
  badge?: () => any
}>();

const attrs = useAttrs();
const wrapperAttrs = computed(() => ({ class: attrs.class, style: attrs.style }));
const buttonAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs;
  return rest;
});

const isInert = computed(() => disabled || loading);

function onClick(event: MouseEvent) {
  if (isInert.value) {
    return;
  }
  emit("click", event);
}
</script>

<template>
  <VTooltip
    v-bind="wrapperAttrs"
    :content="label"
    placement="top"
    theme="verbleif-tooltip-solid"
  >
    <template #reference>
      <span class="inline-flex">
        <button
          v-bind="buttonAttrs"
          type="button"
          class="flex justify-center items-center relative h-8 rounded-full text-text gap-1.5 p-2 transition-colors duration-200 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
          :class="[
            text ? 'w-auto px-2.5' : 'w-8',
            disabled
              ? 'cursor-not-allowed opacity-50 bg-(--input-background) border border-solid border-(--input-border) text-gray-400 dark:text-gray-500'
              : loading
                ? 'cursor-progress bg-(--input-background) border border-solid border-(--input-border) text-gray-800 dark:text-gray-50'
                : active
                  ? 'cursor-pointer border border-solid border-primary-100 bg-primary-50 text-primary-900 hover:bg-primary-100 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50'
                  : 'cursor-pointer bg-(--input-background) border text-gray-800 dark:text-gray-50 border-solid border-(--input-border) hover:border-primary-300 dark:hover:border-primary-400',
          ]"
          :aria-label="label"
          :aria-pressed="toggle ? active : undefined"
          :aria-haspopup="hasPopup ? 'true' : undefined"
          :aria-disabled="disabled ? 'true' : undefined"
          :aria-busy="loading ? 'true' : undefined"
          @click="onClick"
        >
          <VIcon
            v-if="loading"
            class="shrink-0 text-primary-500"
            name="loader-circle"
            spin
            aria-hidden="true"
          />
          <VIcon
            v-else
            class="settings shrink-0"
            :name="icon"
          />
          <span
            v-if="text"
            class="whitespace-nowrap text-xs font-medium tabular-nums"
          >
            {{ text }}
          </span>
          <slot name="badge" />
        </button>
      </span>
    </template>
  </VTooltip>
</template>
